import React, { useCallback, useEffect, useRef, useState } from 'react';
import { PromiseState } from 'react-refetch';
import getExportingJobs from 'url-constructors/Exporting/getExportingJobs';
import {
  IExportJob,
  ViewStates,
  IAvailableCustomerResult,
} from './shared/shared';
import { IAuthenticationProfile } from 'redux/reducers/authentication';
import { ALLOW_EXPORT_GROUP } from './../Header/TopBar/TopBar';
import ViewJob from './Views/ViewJob';
import { useDispatch, useSelector } from 'react-redux';
import ViewContainer from './Views/ViewContainer';
import JobLoader from './Views/JobLoader';
import getExportingJobsHistory from 'url-constructors/Exporting/getExportingJobsHistory';
import getExportingJobsFeedTypes from 'url-constructors/Exporting/getExportingJobsFeedTypes';
import apiConnect from 'helpers/apiConnect';
import ViewJobList from './Views/ViewJobList';
import useJobs from './utils/useJobs';
import CreateJobView from './Views/CreateJobView';
import { loadCountries } from 'redux/reducers/countries';
import { ICountryFlagProps } from 'types/shared';
import startExportingJob from 'url-constructors/Exporting/startExportingJob';
import getAvailableJobCustomers from 'url-constructors/Exporting/getAvailableJobCustomers';
import useCombinedRefetchState from 'hooks/useCombinedRefetchState';
import getExportingJob from 'url-constructors/Exporting/getExportingJob';
import getRuntimeEnv from 'helpers/env';

interface IOuterProps {
  close: () => void;
  userProfile: IAuthenticationProfile;
}

const ADMIN_GROUP = getRuntimeEnv().REACT_APP_OKTA_OPUS_ADMIN_GROUP;

interface IExportRawDataViewProps extends IOuterProps {
  isSmallScreen: boolean;
  fetchAvailableCustomers: () => any;
  fetchExportingJobs: () => any;
  availableCustomers: PromiseState<IAvailableCustomerResult[]>;
  exportingJobs: PromiseState<any>;
  fetchJobsHistory: () => any;
  fetchFeedTypes: () => any;
  jobsHistory: PromiseState<any>;
  feedTypes: PromiseState<any>;
  putUpdateJob: (job: IExportJob) => any;
  putStartJob: (jobId: string, date: Date) => void;
  removeJob: (jobId: string) => void;
}

const dataSources = (): any => {
  return {
    fetchFeedTypes: (): any => {
      return {
        feedTypes: getExportingJobsFeedTypes(),
      };
    },
    fetchAvailableCustomers: (): any => {
      return {
        availableCustomers: getAvailableJobCustomers(),
      };
    },
    fetchExportingJobs: (): any => {
      return {
        exportingJobs: getExportingJobs(),
      };
    },
    fetchJobsHistory: (): any => {
      return {
        jobsHistory: {
          url: getExportingJobsHistory(),
          force: true,
          refreshing: true,
        },
      };
    },
    putUpdateJob: (job: IExportJob): any => {
      return {
        exportingJobs: {
          url: getExportingJob(job?.id),
          method: 'PUT',
          body: JSON.stringify({
            ...job,
            resultFilePrefix: job.resultFilePrefix,
            customerIds: job.customerIds?.map((cid) => Number(cid)),
          }),
          andThen: (): any => {
            return {
              exportingJobs: {
                url: getExportingJobs(),
                force: true,
                refreshing: true,
              },
            };
          },
        },
      };
    },

    putStartJob: (jobId: string, date: Date): any => {
      return {
        jobStatus: {
          url: startExportingJob(jobId, date),
          method: 'PUT',
          andThen: (): any => {
            return {
              jobsHistory: {
                url: getExportingJobsHistory(),
                force: true,
                refreshing: true,
              },
            };
          },
        },
      };
    },
    removeJob: (jobId: string): any => {
      return {
        jobStatus: {
          url: getExportingJob(jobId),
          method: 'DELETE',
          andThen: (): any => {
            return {
              exportingJobs: {
                url: getExportingJobs(),
                force: true,
                refreshing: true,
              },
            };
          },
        },
      };
    },
  };
};

const ExportRawDataView: React.FC<IExportRawDataViewProps> = (props) => {
  const {
    isSmallScreen,
    close,
    fetchExportingJobs,
    fetchJobsHistory,
    fetchAvailableCustomers,
    fetchFeedTypes,
    exportingJobs = PromiseState.create(),
    jobsHistory = PromiseState.create(),
    availableCustomers = PromiseState.create(),
    feedTypes = PromiseState.create(),
    userProfile,
    putUpdateJob,
    putStartJob,
    removeJob,
  } = props;

  const dispatch = useDispatch();

  const hasAdminRole =
    ALLOW_EXPORT_GROUP && userProfile.groups.includes(ALLOW_EXPORT_GROUP);

  const [viewState, setViewState] = useState<ViewStates>(null);
  const [shouldMonitorHistory, setShouldMonitorHistory] =
    useState<boolean>(true);
  const [activeJob, setActiveJob] = useState<IExportJob | undefined>(undefined);

  const intervalRef = useRef<any>(0);

  const intervalTime = viewState === 'JOB_LIST' ? 10000 : 5000;

  const [, pending, ,] = useCombinedRefetchState([
    availableCustomers,
    exportingJobs,
    jobsHistory,
    feedTypes,
  ]);

  useEffect(() => {
    dispatch(loadCountries());
  }, [dispatch]);

  useEffect(() => {
    fetchAvailableCustomers();
  }, [fetchAvailableCustomers]);

  useEffect(() => {
    fetchFeedTypes();
  }, [fetchFeedTypes]);

  const countries = useSelector(
    (state: { countries: { data: ICountryFlagProps[] } }) =>
      state.countries.data
  );

  useEffect(() => {
    fetchExportingJobs();
  }, [fetchExportingJobs]);

  useEffect(() => {
    if (shouldMonitorHistory) {
      fetchJobsHistory();
      intervalRef.current = setInterval(() => {
        fetchJobsHistory();
      }, intervalTime);
    } else {
      clearInterval(intervalRef.current);
    }

    return (): void => {
      clearInterval(intervalRef.current);
    };
  }, [fetchJobsHistory, shouldMonitorHistory, intervalTime]);

  const jobsViewData = useJobs(
    exportingJobs?.value?.data,
    jobsHistory?.value?.data,
    availableCustomers?.value,
    countries
  );

  const subtitle =
    ADMIN_GROUP &&
    userProfile.groups.includes(ADMIN_GROUP) &&
    jobsViewData &&
    jobsViewData?.length > 1 &&
    viewState === 'JOB_LIST'
      ? 'You can see multiple jobs, because you are Admin in Opus'
      : undefined;

  const onSelectJob = useCallback((job?: IExportJob): void => {
    if (job) {
      setActiveJob(job);
      setViewState('VIEW_JOB');
    } else {
      setActiveJob(undefined);
      setViewState('JOB_LIST');
    }
  }, []);

  useEffect(() => {
    if (jobsViewData) {
      if (activeJob)
        return onSelectJob(jobsViewData?.find((vd) => vd.id === activeJob.id));
      if (jobsViewData.length === 1) return onSelectJob(jobsViewData[0]);
      if (jobsViewData.length > 1) return onSelectJob();
      if (jobsViewData.length === 0) return setViewState('CREATE_JOB');
    }
  }, [jobsViewData, activeJob, onSelectJob]);

  const renderSwitch = (): JSX.Element | null => {
    switch (viewState) {
      case 'JOB_LIST':
        return <ViewJobList jobs={jobsViewData} selectJob={onSelectJob} />;
      case 'VIEW_JOB':
        return (
          <ViewJob
            job={activeJob}
            jobCount={jobsViewData?.length || 0}
            isAdmin={hasAdminRole}
            customers={availableCustomers?.value}
            countries={countries}
            saveJob={putUpdateJob}
            startJob={putStartJob}
            backToJobList={onSelectJob}
            shouldGetStatus={setShouldMonitorHistory}
            removeJob={removeJob}
            jobHistory={jobsHistory?.value?.data}
            isSmallScreen={isSmallScreen}
            feedTypes={feedTypes?.value}
          />
        );
      case 'CREATE_JOB':
        return (
          <CreateJobView changeView={(): void => setViewState('VIEW_JOB')} />
        );
      default:
        return null;
    }
  };

  return (
    <ViewContainer title={'Data Export'} subtitle={subtitle} close={close}>
      {pending ? <JobLoader /> : renderSwitch()}
    </ViewContainer>
  );
};

export default apiConnect<IOuterProps>(dataSources)(ExportRawDataView as any);
